import { useEffect, useRef, useState } from 'react';

type UsePnidDrawingParams = {
  fileId?: number;
  drawingId?: string;
  fetchDrawing: (fileId: number) => Promise<string>;
  getEmptyDrawingContent: () => string;
  onReset?: () => void;
};

export const usePnidDrawing = ({
  fileId,
  drawingId,
  fetchDrawing,
  getEmptyDrawingContent,
  onReset,
}: UsePnidDrawingParams) => {
  const [drawing, setDrawing] = useState<string | undefined>(undefined);
  const [drawingFileId, setDrawingFileId] = useState<number | undefined>();

  const fetchDrawingRef = useRef(fetchDrawing);
  const getEmptyDrawingContentRef = useRef(getEmptyDrawingContent);
  const onResetRef = useRef(onReset);

  useEffect(() => {
    fetchDrawingRef.current = fetchDrawing;
    getEmptyDrawingContentRef.current = getEmptyDrawingContent;
    onResetRef.current = onReset;
  }, [fetchDrawing, getEmptyDrawingContent, onReset]);

  useEffect(() => {
    let isCurrentFile = true;

    onResetRef.current?.();
    setDrawing(undefined);
    setDrawingFileId(undefined);

    if (fileId != null) {
      const currentFileId = fileId;

      const loadDrawing = async () => {
        try {
          const drawingContent = await fetchDrawingRef.current(currentFileId);
          if (!isCurrentFile) {
            return;
          }

          setDrawing(drawingContent);
          setDrawingFileId(currentFileId);
        } catch {
          if (!isCurrentFile) {
            return;
          }

          setDrawing(getEmptyDrawingContentRef.current());
          setDrawingFileId(currentFileId);
        }
      };

      loadDrawing();
    }

    return () => {
      isCurrentFile = false;
    };
  }, [fileId, drawingId]);

  return { drawing, drawingFileId, setDrawing };
};
